import { Link } from "react-router-dom";
import { SEOHead } from "@/components/seo/SEOHead";
import { Button } from "@/components/ui/button";
import {
  ArrowRight,
  Zap,
  Shield,
  FileText,
  CheckCircle2,
  Users,
  Check,
  Sparkles,
  TrendingUp,
  Menu,
  X,
} from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { useState } from "react";
import logoImage from "@/assets/logo.png";

const features = [
  {
    icon: Zap,
    title: "Gains instantanés",
    description: "Réalisez vos missions et recevez vos revenus immédiatement.",
  },
  {
    icon: Shield,
    title: "Transactions sécurisées",
    description: "Vos fonds sont protégés par un système fiable et sécurisé.",
  },
  {
    icon: FileText,
    title: "Tarification transparente",
    description: "Aucun frais caché. Vous gagnez exactement ce qui est affiché.",
  },
  {
    icon: CheckCircle2,
    title: "Plateforme fiable",
    description: "Une plateforme qui vous offre une expérience transparente et un support réactif.",
  },
];

const steps = [
  {
    number: "01",
    icon: FileText,
    title: "Créez votre compte",
    description: "Inscrivez-vous en quelques secondes avec votre numéro de téléphone.",
  },
  {
    number: "02",
    icon: CheckCircle2,
    title: "Réalisez vos missions",
    description: "Regardez des vidéos, effectuez des tâches simples et gagnez de l'argent facilement.",
  },
  {
    number: "03",
    icon: Sparkles,
    title: "Retirez vos gains",
    description: "Vos retraits sont envoyés sur Wave ou Orange Money chaque lundi.",
  },
];

const packs = [
  {
    name: "Débutant",
    price: "10 000",
    priceNum: 10000,
    description: "Idéal pour découvrir les missions rémunérées sans risque",
    features: [
      { label: "Revenu quotidien", value: "360 FCFA" },
      { label: "Missions par jour", value: "3" },
      { label: "Revenu/mission", value: "120 FCFA" },
      { label: "Revenu mensuel", value: "10 800 FCFA" },
      { label: "Revenu annuel", value: "129 600 FCFA" },
    ],
    popular: false,
    color: "light",
  },
  {
    name: "Essentiel",
    price: "30 000",
    priceNum: 30000,
    description: "Parfait pour augmenter vos revenus avec plus de missions chaque jour",
    features: [
      { label: "Revenu quotidien", value: "1 050 FCFA" },
      { label: "Missions par jour", value: "5" },
      { label: "Revenu/mission", value: "210 FCFA" },
      { label: "Revenu mensuel", value: "31 500 FCFA" },
      { label: "Revenu annuel", value: "378 000 FCFA" },
    ],
    popular: false,
    color: "light",
  },
  {
    name: "Standard",
    price: "100 000",
    priceNum: 100000,
    description: "Conçu pour maximiser vos gains grâce à des missions mieux rémunérées",
    features: [
      { label: "Revenu quotidien", value: "3 500 FCFA" },
      { label: "Missions par jour", value: "5" },
      { label: "Revenu/mission", value: "700 FCFA" },
      { label: "Revenu mensuel", value: "105 000 FCFA" },
      { label: "Revenu annuel", value: "1 260 000 FCFA" },
    ],
    popular: true,
    color: "dark",
  },
  {
    name: "Premium",
    price: "300 000",
    priceNum: 300000,
    description: "Pour ceux qui veulent des revenus quotidiens sérieux et constants",
    features: [
      { label: "Revenu quotidien", value: "10 500 FCFA" },
      { label: "Missions par jour", value: "6" },
      { label: "Revenu/mission", value: "1 750 FCFA" },
      { label: "Revenu mensuel", value: "315 000 FCFA" },
      { label: "Revenu annuel", value: "3 780 000 FCFA" },
    ],
    popular: false,
    color: "light",
  },
  {
    name: "Avancé",
    price: "1 000 000",
    priceNum: 1000000,
    description: "Un pack puissant pour décupler vos revenus et accélérer votre progression",
    features: [
      { label: "Revenu quotidien", value: "35 040 FCFA" },
      { label: "Missions par jour", value: "8" },
      { label: "Revenu/mission", value: "4 380 FCFA" },
      { label: "Revenu mensuel", value: "1 051 200 FCFA" },
      { label: "Revenu annuel", value: "12 614 400 FCFA" },
    ],
    popular: false,
    color: "light",
  },
  {
    name: "Pro Plus",
    price: "2 000 000",
    priceNum: 2000000,
    description: "Pensé pour les utilisateurs ambitieux visant des revenus élevés chaque jour",
    features: [
      { label: "Revenu quotidien", value: "70 000 FCFA" },
      { label: "Missions par jour", value: "10" },
      { label: "Revenu/mission", value: "7 000 FCFA" },
      { label: "Revenu mensuel", value: "2 100 000 FCFA" },
      { label: "Revenu annuel", value: "25 200 000 FCFA" },
    ],
    popular: false,
    color: "light",
  },
  {
    name: "Elite",
    price: "5 000 000",
    priceNum: 5000000,
    description: "Des gains très élevés pour ceux qui veulent passer au niveau supérieur",
    features: [
      { label: "Revenu quotidien", value: "180 000 FCFA" },
      { label: "Missions par jour", value: "12" },
      { label: "Revenu/mission", value: "15 000 FCFA" },
      { label: "Revenu mensuel", value: "5 400 000 FCFA" },
      { label: "Revenu annuel", value: "64 800 000 FCFA" },
    ],
    popular: false,
    color: "light",
  },
  {
    name: "Ultimate",
    price: "10 000 000",
    priceNum: 10000000,
    description: "Pour les utilisateurs professionnels cherchant le rendement maximal",
    features: [
      { label: "Revenu quotidien", value: "400 050 FCFA" },
      { label: "Missions par jour", value: "15" },
      { label: "Revenu/mission", value: "26 670 FCFA" },
      { label: "Revenu mensuel", value: "12 001 500 FCFA" },
      { label: "Revenu annuel", value: "144 018 000 FCFA" },
    ],
    popular: false,
    color: "light",
  },
  {
    name: "Master",
    price: "20 000 000",
    priceNum: 20000000,
    description: "Le pack le plus puissant. Revenus maximum pour les investisseurs sérieux",
    features: [
      { label: "Revenu quotidien", value: "900 000 FCFA" },
      { label: "Missions par jour", value: "20" },
      { label: "Revenu/mission", value: "45 000 FCFA" },
      { label: "Revenu mensuel", value: "27 000 000 FCFA" },
      { label: "Revenu annuel", value: "324 000 000 FCFA" },
    ],
    popular: false,
    color: "light",
  },
];

const faqs = [
  {
    question: "Comment je gagne de l'argent sur AfriqueCash ?",
    answer: "Vous gagnez en complétant des petites tâches digitales simples depuis l'application. Regarder des vidéos, compléter des offres, etc. Chaque tâche validée vous rapporte des gains en FCFA.",
  },
  {
    question: "Comment fonctionne le programme d'affiliation ?",
    answer: "Vous recevez 20% de commission sur chaque achat de pack par un filleul parrainé avec votre lien. De plus, vous gagnez des points qui peuvent être convertis en argent.",
  },
  {
    question: "Combien je peux gagner par jour ?",
    answer: "Vos gains dépendent du pack que vous avez acheté. Chaque pack propose un nombre de missions par jour et un revenu maximal journalier. Par exemple, le pack Débutant offre 750 FCFA par jour.",
  },
  {
    question: "Est-ce que AfriqueCash est sécurisé ?",
    answer: "Oui, bien sûr ! Vos transactions et vos données sont protégées. Nous utilisons les méthodes de paiement officielles (MTN, Orange, Wave) et vos données sont stockées de façon sécurisée.",
  },
  {
    question: "Comment fonctionnent les retraits ?",
    answer: "Vos retraits sont traités chaque semaine le lundi. Vous demandez un retrait via l'application et les fonds sont envoyés sur votre compte Wave ou Orange Money après validation manuelle.",
  },
  {
    question: "Quels moyens de paiement puis-je utiliser pour déposer ?",
    answer: "MTN Mobile Money, Orange Money et Wave. Effectuez un dépôt vers le numéro affiché et importez la preuve de paiement dans l'application.",
  },
  {
    question: "Comment gagner de l'argent sur la plateforme ?",
    answer: "Inscrivez-vous, achetez un pack de missions, puis commencez à réaliser vos missions quotidiennes. Les gains seront ajoutés à votre portefeuille après validation.",
  },
  {
    question: "Combien de temps pour qu'un dépôt soit validé ?",
    answer: "Les dépôts sont vérifiés manuellement et peuvent être validés entre quelques heures et 24 heures maximum.",
  },
  {
    question: "Qu'est-ce que le système d'affiliation ?",
    answer: "Vous recevez une commission unique de 20% sur l'achat d'un pack par un filleul parrainé avec votre lien. De plus, vous gagnez des points que vous pouvez convertir en argent réel depuis la section Points.",
  },
];

export default function Index() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <>
      <SEOHead
        title="AfriqueCash - Gagnez de l'argent avec des missions rémunérées en Afrique"
        description="Plateforme américaine de missions rémunérées. Réalisez des tâches digitales simples et recevez vos gains via Wave, Orange Money et MTN. Société enregistrée aux USA."
        canonical="/"
      />
      <div className="min-h-screen bg-background overflow-x-hidden">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-background/95 backdrop-blur-md border-b border-border/50">
        <div className="container mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          {/* Logo */}
          <div className="flex items-center gap-2">
            <img src={logoImage} alt="AfriqueCash" className="h-7 sm:h-8 w-auto" width={152} height={32} loading="eager" />
          </div>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            <a href="#" className="text-sm font-medium text-foreground hover:text-primary transition-colors">
              Accueil
            </a>
            <a href="#packs" className="text-sm font-medium text-muted-foreground hover:text-primary transition-colors">
              Nos Packs
            </a>
            <a href="#faq" className="text-sm font-medium text-muted-foreground hover:text-primary transition-colors">
              FAQ
            </a>
            <Link to="/legal" className="text-sm font-medium text-muted-foreground hover:text-primary transition-colors">
              Mentions Légales
            </Link>
            <Link to="/contact" className="text-sm font-medium text-muted-foreground hover:text-primary transition-colors">
              Contactez-Nous
            </Link>
          </div>

          {/* Desktop Buttons */}
          <div className="hidden sm:flex items-center gap-3">
            <Link to="/auth">
              <Button variant="ghost" className="rounded-full px-5 text-sm">
                Se connecter
              </Button>
            </Link>
            <Link to="/auth">
              <Button className="rounded-full px-5 text-sm shadow-lg shadow-primary/20 hover:shadow-primary/30 transition-all">
                S'inscrire gratuitement
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="sm:hidden p-2 rounded-lg hover:bg-muted transition-colors"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="sm:hidden bg-background border-t border-border/50 animate-fade-in">
            <div className="container mx-auto px-4 py-4 space-y-4">
              <div className="space-y-2">
                <a 
                  href="#" 
                  className="block py-2 text-sm font-medium text-foreground"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Accueil
                </a>
                <a 
                  href="#packs" 
                  className="block py-2 text-sm font-medium text-muted-foreground"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Nos Packs
                </a>
                <a 
                  href="#faq" 
                  className="block py-2 text-sm font-medium text-muted-foreground"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  FAQ
                </a>
                <Link 
                  to="/legal" 
                  className="block py-2 text-sm font-medium text-muted-foreground"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Mentions Légales
                </Link>
                <Link 
                  to="/contact" 
                  className="block py-2 text-sm font-medium text-muted-foreground"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Contactez-Nous
                </Link>
              </div>
              <div className="pt-4 border-t border-border/50 space-y-3">
                <Link to="/auth" className="block">
                  <Button variant="outline" className="w-full rounded-full">
                    Se connecter
                  </Button>
                </Link>
                <Link to="/auth" className="block">
                  <Button className="w-full rounded-full shadow-lg shadow-primary/20">
                    S'inscrire gratuitement
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        )}
      </nav>
      {/* Main Content */}
      <main>
      {/* Hero Section */}
      <section className="pt-24 pb-16 sm:pt-28 sm:pb-20 md:pt-36 md:pb-28 px-4 sm:px-6 overflow-hidden relative">
        {/* Background decorative elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-20 left-10 w-64 h-64 sm:w-96 sm:h-96 bg-primary/5 rounded-full blur-3xl" />
          <div className="absolute bottom-20 right-10 w-48 h-48 sm:w-80 sm:h-80 bg-primary/8 rounded-full blur-3xl" />
        </div>

        <div className="container mx-auto relative">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            {/* Left Content */}
            <div className="order-2 lg:order-1 animate-fade-in text-center lg:text-left">
              <span className="inline-block text-primary text-sm sm:text-base font-semibold uppercase tracking-wider mb-4 sm:mb-5">
                AFRIQUE CASH
              </span>
              
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-5xl xl:text-6xl font-bold text-foreground mb-5 sm:mb-7 leading-[1.15] tracking-tight">
                Gagnez de l'argent facilement grâce aux{" "}
                <span className="text-primary">missions rémunérées</span>
              </h1>

              <p className="text-base sm:text-lg md:text-xl text-muted-foreground mb-8 sm:mb-10 max-w-xl mx-auto lg:mx-0 leading-relaxed">
                Réalisez de petites tâches digitales et recevez vos gains instantanément où que vous soyez en Afrique.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <Button asChild size="lg" className="w-full sm:w-auto rounded-full px-8 sm:px-10 py-6 gap-2 text-base sm:text-lg font-semibold shadow-xl shadow-primary/30 hover:shadow-primary/50 hover:scale-[1.02] transition-all duration-300">
                  <Link to="/auth">
                    Commencer maintenant
                    <ArrowRight className="h-5 w-5" />
                  </Link>
                </Button>
                <Button asChild size="lg" variant="outline" className="w-full sm:w-auto rounded-full px-8 sm:px-10 py-6 text-base sm:text-lg font-medium border-2 hover:bg-muted/50">
                  <Link to="/auth">
                    J'ai déjà un compte
                  </Link>
                </Button>
              </div>

              <div className="flex items-center gap-3 mt-8 text-sm sm:text-base text-muted-foreground justify-center lg:justify-start">
                <div className="flex items-center justify-center w-6 h-6 rounded-full bg-primary/15 ring-2 ring-primary/20">
                  <Check className="h-3.5 w-3.5 text-primary" />
                </div>
                <span>Votre revenu quotidien démarre ici</span>
              </div>
            </div>

            {/* Right Content - Premium Hero Image */}
            <div className="order-1 lg:order-2 relative animate-fade-in" style={{ animationDelay: "0.2s" }}>
              <div className="relative max-w-[280px] sm:max-w-[340px] mx-auto lg:max-w-[380px] lg:ml-auto">
                {/* Main Hero Image Container with Premium Gradient Border */}
                <div className="relative">
                  {/* Glow effect behind */}
                  <div className="absolute -inset-4 bg-gradient-to-br from-primary/30 via-primary/10 to-transparent rounded-3xl blur-2xl opacity-60" />
                  
                  {/* Main container */}
                  <div className="relative bg-gradient-to-br from-primary/10 via-card to-card rounded-[2rem] p-1 shadow-2xl shadow-primary/10 overflow-hidden border border-primary/20">
                    {/* Inner container with image */}
                    <div className="relative bg-gradient-to-b from-muted/50 to-muted/30 rounded-[1.75rem] overflow-hidden">
                      {/* Decorative pattern overlay */}
                      <div className="absolute inset-0 opacity-[0.03]" style={{
                        backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
                      }} />
                      
                      {/* Hero Man Image */}
                      <div className="relative z-10">
                        <img 
                          src="/hero-man.png" 
                          alt="Utilisateur AfriqueCash heureux" 
                          className="w-full h-auto aspect-[4/5] object-cover object-top"
                          width={370}
                          height={463}
                          fetchPriority="high"
                          loading="eager"
                        />
                        
                        {/* Gradient overlay at bottom */}
                        <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-card/90 via-card/40 to-transparent" />
                      </div>
                    </div>
                  </div>

                  {/* Floating Card - Top Right - Users Count */}
                  <div className="absolute -top-2 -right-2 sm:top-4 sm:-right-4 bg-card/95 backdrop-blur-md rounded-xl sm:rounded-2xl shadow-2xl p-3 sm:p-4 border border-border/40 animate-float z-20">
                    <div className="flex items-center gap-2 sm:gap-3">
                      <div className="flex -space-x-2">
                        <div className="w-7 h-7 sm:w-9 sm:h-9 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center ring-2 ring-card text-white font-bold text-[10px] sm:text-xs">A</div>
                        <div className="w-7 h-7 sm:w-9 sm:h-9 rounded-full bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center ring-2 ring-card text-white font-bold text-[10px] sm:text-xs">K</div>
                        <div className="w-7 h-7 sm:w-9 sm:h-9 rounded-full bg-gradient-to-br from-primary to-primary/80 flex items-center justify-center ring-2 ring-card text-white font-bold text-[10px] sm:text-xs">M</div>
                      </div>
                      <div>
                        <p className="font-bold text-foreground text-sm sm:text-base">30K+</p>
                        <p className="text-[10px] sm:text-xs text-muted-foreground leading-tight">Utilisateurs actifs</p>
                      </div>
                    </div>
                  </div>

                  {/* Stats Card - Bottom Left - Earnings */}
                  <div className="absolute -bottom-3 -left-3 sm:bottom-8 sm:-left-6 bg-card/95 backdrop-blur-md rounded-xl sm:rounded-2xl shadow-2xl p-3 sm:p-5 border border-border/40 animate-float z-20" style={{ animationDelay: "1.5s" }}>
                    <div className="flex items-center gap-3 sm:gap-4">
                      <div className="w-10 h-10 sm:w-14 sm:h-14 rounded-xl sm:rounded-2xl bg-gradient-to-br from-primary/20 to-primary/10 flex items-center justify-center">
                        <TrendingUp className="w-5 h-5 sm:w-7 sm:h-7 text-primary" />
                      </div>
                      <div>
                        <p className="text-[10px] sm:text-xs text-muted-foreground">Revenus Totaux Générés</p>
                        <p className="font-bold text-foreground text-base sm:text-xl">753.456.000 <span className="text-xs sm:text-sm font-semibold">FCFA</span></p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16 sm:py-20 md:py-28 px-4 sm:px-6">
        <div className="container mx-auto">
          <div className="text-center mb-10 sm:mb-14">
            <span className="inline-block text-primary text-sm sm:text-base font-semibold uppercase tracking-wider mb-3 sm:mb-4">
              POURQUOI NOUS CHOISIR ?
            </span>
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-foreground leading-tight">
              Pourquoi des milliers d'Africains<br className="hidden sm:block" /> choisissent AfriqueCash ?
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5 md:gap-6">
            {features.map((feature, index) => (
              <div
                key={index}
                className="group bg-card rounded-2xl sm:rounded-3xl p-5 sm:p-6 md:p-7 border border-border/50 hover:border-primary/40 hover:shadow-2xl hover:shadow-primary/10 transition-all duration-300 hover:-translate-y-1"
              >
                <div className="flex h-12 w-12 sm:h-14 sm:w-14 items-center justify-center rounded-xl sm:rounded-2xl bg-primary/10 mb-4 sm:mb-5 group-hover:bg-primary/20 group-hover:scale-110 transition-all duration-300">
                  <feature.icon className="h-6 w-6 sm:h-7 sm:w-7 text-primary" />
                </div>
                <h3 className="text-base sm:text-lg md:text-xl font-semibold text-foreground mb-2 sm:mb-3">
                  {feature.title}
                </h3>
                <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 sm:py-20 md:py-28 px-4 sm:px-6 bg-gradient-to-b from-[hsl(220,25%,12%)] to-[hsl(220,25%,8%)]">
        <div className="container mx-auto">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6 sm:gap-8 mb-10 sm:mb-14 text-center lg:text-left">
            <div>
              <span className="inline-block text-primary text-sm sm:text-base font-semibold uppercase tracking-wider mb-3 sm:mb-4">
                COMMENT ÇA MARCHE ?
              </span>
              <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white leading-tight">
                Gagnez de l'argent en 3 étapes simples
              </h2>
            </div>
            <Link to="/auth" className="inline-block">
              <Button size="lg" className="rounded-full px-8 whitespace-nowrap shadow-lg shadow-primary/25 text-base font-semibold">
                Commencer maintenant
              </Button>
            </Link>
          </div>

          <div className="space-y-4 sm:space-y-0 sm:bg-card sm:rounded-2xl md:rounded-3xl sm:p-6 md:p-10 sm:border sm:border-border/10 sm:shadow-2xl">
            <div className="grid sm:grid-cols-3 gap-4 sm:gap-8">
              {steps.map((step, index) => (
                <div key={index} className="bg-card sm:bg-transparent rounded-2xl sm:rounded-none p-5 sm:p-0 border border-border/20 sm:border-none flex flex-col items-center text-center relative">
                  {/* Step number */}
                  <div className="text-6xl sm:text-7xl font-bold text-primary/10 mb-2 sm:mb-3 leading-none">
                    {step.number}
                  </div>
                  <div className="flex h-14 w-14 sm:h-16 sm:w-16 items-center justify-center rounded-xl sm:rounded-2xl bg-primary/10 text-primary mb-4 sm:mb-5">
                    <step.icon className="h-7 w-7 sm:h-8 sm:w-8" />
                  </div>
                  <h3 className="text-lg sm:text-xl font-semibold text-foreground mb-2 sm:mb-3">
                    {step.title}
                  </h3>
                  <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                    {step.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Packs Section */}
      <section id="packs" className="py-16 sm:py-20 md:py-28 px-4 sm:px-6">
        <div className="container mx-auto">
          <div className="text-center mb-10 sm:mb-14">
            <span className="inline-block text-primary text-sm sm:text-base font-semibold uppercase tracking-wider mb-3 sm:mb-4">
              NOS PACKS DE MISSIONS
            </span>
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-foreground leading-tight">
              Choisissez un Pack selon vos objectifs et<br className="hidden sm:block" /> augmentez vos revenus chaque jour.
            </h2>
          </div>

          <div className="grid gap-5 sm:gap-6">
            {packs.map((pack, index) => (
              <div
                key={index}
                className={`group relative rounded-2xl sm:rounded-3xl p-5 sm:p-7 border transition-all duration-300 hover:shadow-2xl ${
                  pack.color === "dark"
                    ? "bg-[hsl(220,25%,12%)] border-[hsl(220,25%,20%)] shadow-xl"
                    : "bg-card border-border/50 hover:border-primary/30"
                }`}
              >
                {pack.popular && (
                  <div className="absolute top-4 right-4 sm:top-5 sm:right-5">
                    <span className="bg-primary text-white text-xs sm:text-sm font-semibold px-3 sm:px-4 py-1.5 sm:py-2 rounded-full shadow-lg">
                      Le plus populaire
                    </span>
                  </div>
                )}

                <div className={`flex h-11 w-11 sm:h-14 sm:w-14 items-center justify-center rounded-xl sm:rounded-2xl mb-4 sm:mb-5 ${
                  pack.color === "dark" ? "bg-primary" : "bg-primary/10"
                }`}>
                  <Sparkles className={`h-5 w-5 sm:h-6 sm:w-6 ${pack.color === "dark" ? "text-white" : "text-primary"}`} />
                </div>

                <h3 className={`text-lg sm:text-xl font-bold mb-2 ${pack.color === "dark" ? "text-white" : "text-foreground"}`}>
                  {pack.name}
                </h3>
                <p className={`text-sm sm:text-base mb-4 sm:mb-5 ${pack.color === "dark" ? "text-gray-400" : "text-muted-foreground"}`}>
                  {pack.description}
                </p>

                <div className="mb-5 sm:mb-7">
                  <span className={`text-3xl sm:text-4xl font-bold ${pack.color === "dark" ? "text-white" : "text-foreground"}`}>
                    {pack.price}
                  </span>
                  <span className={`text-sm sm:text-base ml-2 ${pack.color === "dark" ? "text-gray-400" : "text-muted-foreground"}`}>
                    FCFA
                  </span>
                </div>

                <div className="space-y-3 sm:space-y-4 mb-5 sm:mb-7">
                  {pack.features.map((feature, fIndex) => (
                    <div key={fIndex} className="flex items-center gap-3">
                      <div className="flex-shrink-0 w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-primary/20 flex items-center justify-center">
                        <Check className="h-3 w-3 sm:h-3.5 sm:w-3.5 text-primary" />
                      </div>
                      <span className={`text-sm sm:text-base ${pack.color === "dark" ? "text-gray-300" : "text-muted-foreground"}`}>
                        {feature.label} : <span className={`font-semibold ${pack.color === "dark" ? "text-white" : "text-foreground"}`}>{feature.value}</span>
                      </span>
                    </div>
                  ))}
                </div>

                <Link to="/auth" className="block">
                  <Button 
                    size="lg"
                    className={`w-full rounded-full text-base font-semibold transition-all duration-300 ${
                      pack.color === "dark" 
                        ? "bg-primary hover:bg-primary/90 shadow-lg shadow-primary/25" 
                        : "bg-primary hover:bg-primary/90"
                    }`}
                  >
                    Commencer maintenant
                  </Button>
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Affiliate Section */}
      <section className="py-16 sm:py-20 md:py-28 px-4 sm:px-6">
        <div className="container mx-auto">
          <div className="relative bg-gradient-to-br from-primary/15 via-primary/8 to-primary/3 rounded-3xl sm:rounded-[2rem] p-6 sm:p-10 md:p-14 overflow-hidden border border-primary/10">
            {/* Decorative elements */}
            <div className="absolute top-0 right-0 w-64 h-64 bg-primary/10 rounded-full blur-3xl" />
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-primary/15 rounded-full blur-2xl" />
            
            <div className="relative z-10 text-center max-w-2xl mx-auto">
              <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-5 sm:mb-7 leading-tight">
                Gagnez encore plus d'argent grâce à l'affiliation
              </h2>
              <p className="text-muted-foreground mb-8 sm:mb-10 leading-relaxed text-base sm:text-lg md:text-xl">
                Invitez vos amis et gagnez <strong className="text-primary">20%</strong> du montant du Pack qu'ils achètent. Partagez votre lien de parrainage, faites découvrir AfriqueCash autour de vous et augmentez vos revenus sans effort.
              </p>
              <Link to="/auth">
                <Button size="lg" className="rounded-full px-10 sm:px-12 py-6 text-base sm:text-lg font-semibold shadow-xl shadow-primary/30 hover:shadow-primary/50 transition-all">
                  Commencer maintenant
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-16 sm:py-20 md:py-28 px-4 sm:px-6 bg-muted/30">
        <div className="container mx-auto max-w-4xl">
          <div className="text-center mb-10 sm:mb-14">
            <span className="inline-block text-primary text-sm sm:text-base font-semibold uppercase tracking-wider mb-3 sm:mb-4">
              FAQS
            </span>
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-foreground leading-tight">
              Des questions ? Nous avons<br className="hidden sm:block" /> les réponses.
            </h2>
          </div>

          <div className="space-y-3 sm:space-y-4">
            {faqs.map((faq, index) => (
              <Accordion key={index} type="single" collapsible className="bg-card rounded-xl sm:rounded-2xl border border-border/30 overflow-hidden shadow-lg">
                <AccordionItem value={`item-${index}`} className="border-none">
                  <AccordionTrigger className="px-5 sm:px-6 py-4 sm:py-5 text-left text-sm sm:text-base font-semibold hover:no-underline hover:bg-muted/30 transition-colors">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="px-5 sm:px-6 pb-4 sm:pb-5 text-sm sm:text-base text-muted-foreground leading-relaxed">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            ))}
          </div>
        </div>
      </section>
      </main>

      {/* Footer */}
      <footer id="contact" className="py-12 sm:py-16 px-4 sm:px-6 bg-background border-t border-border">
        <div className="container mx-auto">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 sm:gap-10 mb-10 sm:mb-12">
            {/* Brand */}
            <div className="col-span-2 lg:col-span-1">
              <img src={logoImage} alt="AfriqueCash" className="h-8 sm:h-10 w-auto mb-4 sm:mb-5" />
              <p className="text-sm sm:text-base text-muted-foreground mb-4 sm:mb-5 max-w-xs leading-relaxed">
                AfriqueCash permet aux utilisateurs d'effectuer de petites tâches en ligne et de gagner de l'argent chaque jour, en toute simplicité et sécurité.
              </p>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="font-bold text-foreground mb-4 sm:mb-5 text-base sm:text-lg">Liens rapides</h4>
              <ul className="space-y-3 sm:space-y-4">
                <li><a href="#" className="text-sm sm:text-base text-muted-foreground hover:text-primary transition-colors">Accueil</a></li>
                <li><a href="#packs" className="text-sm sm:text-base text-muted-foreground hover:text-primary transition-colors">Nos Packs</a></li>
                <li><a href="#faq" className="text-sm sm:text-base text-muted-foreground hover:text-primary transition-colors">FAQ</a></li>
                <li><Link to="/auth" className="text-sm sm:text-base text-muted-foreground hover:text-primary transition-colors">Connexion/Inscription</Link></li>
                <li><Link to="/contact" className="text-sm sm:text-base text-muted-foreground hover:text-primary transition-colors">Contactez Nous</Link></li>
              </ul>
            </div>

            {/* Legal */}
            <div>
              <h4 className="font-bold text-foreground mb-4 sm:mb-5 text-base sm:text-lg">Légal</h4>
              <ul className="space-y-3 sm:space-y-4">
                <li><Link to="/legal" className="text-sm sm:text-base text-muted-foreground hover:text-primary transition-colors">Mentions Légales</Link></li>
                <li><Link to="/terms" className="text-sm sm:text-base text-muted-foreground hover:text-primary transition-colors">Conditions d'utilisation</Link></li>
                <li><Link to="/privacy" className="text-sm sm:text-base text-muted-foreground hover:text-primary transition-colors">Politique de confidentialité</Link></li>
              </ul>
            </div>

            {/* CTA */}
            <div className="col-span-2 lg:col-span-1">
              <div className="bg-gradient-to-br from-primary/15 to-primary/5 rounded-2xl p-5 sm:p-6 border border-primary/10">
                <p className="text-foreground font-semibold mb-3 text-base sm:text-lg">Commencez à gagner aujourd'hui !</p>
                <Link to="/auth">
                  <Button className="w-full rounded-full shadow-lg shadow-primary/25 text-sm sm:text-base">
                    S'inscrire gratuitement
                  </Button>
                </Link>
              </div>
            </div>
          </div>

          <div className="border-t border-border pt-8 text-center">
            <p className="text-sm sm:text-base text-muted-foreground">
              © 2025 AfriqueCash. Tous droits réservés.
            </p>
          </div>
        </div>
      </footer>
      </div>
    </>
  );
}
